import type { CommandResult, Lang, UiHint, WidgetEvent } from "./api";
import { cinemaDate } from "./cinema-time";
import { money, paymentMethodLabel } from "./i18n";

export type UserActivityClock = { lastUserAt: number; lastProviderPingAt: number };

/** Every customer event refreshes inactivity; bursts need only one provider ping per second. */
export function recordUserActivity(clock: UserActivityClock, notifyProvider?: () => void, now = Date.now()): void {
  clock.lastUserAt = now;
  if (notifyProvider && now - clock.lastProviderPingAt >= 1000) {
    clock.lastProviderPingAt = now;
    notifyProvider();
  }
}

/** A client tool may claim the map opened only after its verified response is rendered. */
export async function renderVerifiedSeatMap(load: () => Promise<CommandResult>, render: (ui: UiHint) => void, isCurrent: () => boolean) {
  try {
    const result = await load();
    if (!isCurrent()) return { ok: false, rendered: false, error: "The widget session changed. Check the current booking again." };
    if (!result.ok || result.ui?.type !== "seatmap") return { ok: false, rendered: false, error: result.error ?? "No verified seat map was returned." };
    render(result.ui);
    return { ok: true, rendered: true };
  } catch {
    return { ok: false, rendered: false, error: "The seat map could not load. Please try again." };
  }
}

/** Direct navigation confirms visible UI; a paused conversation must stay paused. */
export function directSeatMapFeedback(command: string, result: CommandResult, lang: Lang, connected: boolean, currentSession: boolean) {
  if (command !== "seat.plan" || !currentSession || !result.ok || result.ui?.type !== "seatmap") return;
  return {
    text: lang === "ar" ? "خريطة المقاعد جاهزة. يمكنك الآن اختيار مقاعدك." : "Seat map ready. You can choose your seats now.",
    context: connected ? JSON.stringify({ action: "seat_map_opened", succeeded: true, rendered: true, bookingChanged: false,
      instruction: "The seat map is already visible. Give one brief acknowledgement without calling tools, reopening the map or changing/holding seats. Wait for the customer." }) : undefined,
  };
}

/** Consume transport positions even when a relink repeats an already displayed event. */
export function acceptWidgetEvent(sequences: Map<string, number>, identities: Set<string>, conversationId: string, event: WidgetEvent): boolean {
  if (event.seq <= (sequences.get(conversationId) ?? 0)) return false;
  sequences.set(conversationId, event.seq);
  if (event.eventId) {
    if (identities.has(event.eventId)) return false;
    identities.add(event.eventId);
  }
  return true;
}

/** Translate only fixed controls; movie, cinema and offer names keep their server-provided wording. */
export function uiActionLabel(value: string, fallback: string, lang: Lang): string {
  const labels: Record<string, [string, string]> = {
    "recover:confirm": ["Check and hold seats again", "تحقق واحجز المقاعد مجدداً"],
    "booking:restart": ["Another show", "عرض آخر"],
    "seats:open": ["Choose seats", "اختيار المقاعد"],
    "seatmap:open": ["Choose seats", "اختيار المقاعد"],
    "menu:open": ["Browse menu", "تصفح القائمة"],
    "fnb:suggest": ["Add snacks", "إضافة وجبات خفيفة"],
    "pay:start": ["Continue to checkout", "المتابعة للدفع"],
    fnb_skip: ["Skip snacks", "تخطي الوجبات الخفيفة"],
    fnb_usual: ["Same as last time", "نفس الطلب السابق"],
    "usual:no": ["Another cinema", "سينما أخرى"],
    abort: ["Go back", "الرجوع"],
  };
  const label = labels[value] ?? labels[value.split(":")[0]!];
  return label?.[lang === "ar" ? 1 : 0] ?? fallback;
}

/** Summaries use only display fields, never session/order identifiers or hidden account data. */
export function decisionSummary(ui: UiHint, lang: Lang): string {
  const ar = lang === "ar";
  const meta = ui.meta ?? {};
  const items = ui.items ?? [];
  const selected = meta.selectedSummary ?? meta.selectedSession ?? meta.selectedFilm;
  const item = selected ?? (items.length === 1 || ["order", "payment", "booking", "qr"].includes(ui.type) ? items[0] : undefined) ?? {};
  const names = (values: unknown[]) => [...new Set(values.filter((value): value is string => typeof value === "string" && !!value.trim()))].slice(0, 2).join(" / ");
  const film = item.filmTitle ?? item.title ?? meta.film?.title ?? meta.filmTitle;
  const cinema = item.cinemaName ?? meta.cinemaName;
  const rawTime = item.showtime ?? meta.showtime;
  const parsed = typeof rawTime === "string" ? cinemaDate(rawTime) : null;
  const when = parsed && Number.isFinite(parsed.getTime())
    ? new Intl.DateTimeFormat(ar ? "ar-AE" : "en-AE", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Asia/Dubai" }).format(parsed)
    : item.showtimeLabel ?? [item.dateLabel, item.time].filter(Boolean).join(" ");
  const count = item.tickets?.length || item.ticketCount || meta.selectedTicketCount;
  const seats = typeof item.seats === "string" ? item.seats : Array.isArray(item.seats) ? item.seats.join(", ") : item.tickets?.map((ticket: any) => ticket.seat).filter(Boolean).join(", ") ?? meta.selectedSeats;
  const details = [film, cinema, when, count ? ar ? `${count} تذاكر` : `${count} ticket${count === 1 ? "" : "s"}` : null, seats ? `${ar ? "المقاعد" : "Seats"} ${seats}` : null].filter(Boolean);
  const amount = (value: unknown) => typeof value === "number" && Number.isFinite(value) ? money(value, lang) : undefined;
  if (ui.type === "payment_switch") return [ar ? "تغيير العرض" : "Offer change", amount(item.currentTotalCents), "→", amount(item.totalAfterCents), item.nextPaymentMethod ? paymentMethodLabel(item.nextPaymentMethod, lang) : undefined].filter(Boolean).join(" · ");
  if (item.swapTo) {
    const swap = item.swapTo;
    details.push(...["→", swap.cinemaName, swap.showtimeLabel, swap.experience, Array.isArray(swap.seats) ? swap.seats.join(", ") : undefined, typeof swap.differenceCents === "number" ? `${ar ? "فرق السعر" : "Price difference"} ${amount(swap.differenceCents)}` : undefined].filter((value): value is string => typeof value === "string"));
  }
  if (item.refund) details.push(...[ar ? "الاسترداد" : "Refund", amount(item.refund.amountCents), item.refund.method, item.refund.eta, item.refund.validityDays ? `${item.refund.validityDays} ${ar ? "يوماً" : "days"}` : undefined].filter((value): value is string => typeof value === "string"));
  if (["order", "payment", "booking", "booking_proposal", "qr", "quantity", "seatmap"].includes(ui.type) && details.length) return details.join(" · ");
  if (["movie", "recommendation"].includes(ui.type)) return film ?? `${ar ? "خيارات الأفلام" : "Movie options"}${items.length ? ` · ${names(items.map((movie) => movie.title ?? movie.filmTitle))}` : ""}`;
  if (ui.type === "showtimes") return selected && details.length ? details.join(" · ") : [ar ? "مواعيد العرض" : "Showtimes", film ?? names(items.map((show) => show.filmTitle)), cinema ?? names(items.map((show) => show.cinemaName))].filter(Boolean).join(" · ");
  if (ui.type === "menu") {
    const food = item.concessions ?? items.filter((food) => Number(food.quantity) > 0);
    return [ar ? "المأكولات والمشروبات" : "Food & drinks", food.map((food: any) => `${food.quantity}× ${ar ? food.descriptionAlt || food.description || food.name : food.description || food.name}`).join(" · ")].filter(Boolean).join(" · ");
  }
  const labels: Record<string, [string, string]> = { quantity: ["Ticket quantity", "عدد التذاكر"], seatmap: ["Seat selection", "اختيار المقاعد"], order: ["Booking choices", "اختيارات الحجز"], payment: ["Payment review", "مراجعة الدفع"], booking: ["Booking", "الحجز"], qr: ["Confirmed booking", "حجز مؤكد"], offer: ["Offers", "العروض"], cinema: ["Cinemas", "دور السينما"], loyalty: ["Your rewards", "مكافآتك"], transfer: ["Customer support", "خدمة العملاء"] };
  return labels[ui.type]?.[ar ? 1 : 0] ?? (ar ? "اختيارات سابقة" : "Earlier choices");
}

export type TranscriptBody =
  | { kind: "msg"; role: "user" | "agent" | "human"; text: string; who?: string }
  /** `archived` cards stay on screen as a record of that step; their controls are switched off. */
  | { kind: "cards"; ui: UiHint; archived?: boolean }
  | { kind: "note"; text: string; polite?: boolean }
  | { kind: "feedback" };
export type TranscriptItem = TranscriptBody & { id: string };

/** Booking steps in order. A later (or repeated) step freezes the earlier cards of the same basket. */
const STEP_RANK: Record<string, number> = {
  quantity: 1,
  booking_proposal: 1,
  seatmap: 2,
  menu: 3,
  order: 3,
  payment_switch: 4,
  payment_switch_confirmation: 4,
  balance_split_confirmation: 4,
  balance_reset_confirmation: 4,
  payment: 4,
  qr: 5,
  refund_options: 2,
  booking: 2,
};

/** Cards that belong to one basket (order) or one booking share a journey key; lists and discovery cards have none. */
export function journeyKey(ui: UiHint): string | undefined {
  const meta = ui.meta ?? {};
  const first = ui.items?.[0] ?? {};
  const order = meta.userSessionId ?? first.userSessionId;
  if (typeof order === "string" && order) return `order:${order}`;
  if (["booking", "refund_options"].includes(ui.type) && !meta.pickList && ui.items?.length === 1) {
    const bookingId = meta.bookingId ?? first.bookingId ?? meta.film?.bookingId;
    if (typeof bookingId === "string" && bookingId) return `booking:${bookingId}`;
  }
  return undefined;
}

/** Key-order-independent serialisation: the same card arrives via the HTTP reply and via the event stream
 * (stored as jsonb, which re-orders object keys), and both must fingerprint identically. */
export function cardFingerprint(value: unknown): string {
  return JSON.stringify(value, (_key, v) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(Object.keys(v as Record<string, unknown>).sort().map((k) => [k, (v as Record<string, unknown>)[k]]))
      : v,
  );
}

/**
 * Transcript cards are append-only: every result adds a card and nothing above it changes.
 * A new step of the same journey freezes the earlier cards of that journey (same or earlier step),
 * so a stale seat map or ticket-quantity card can't act on a basket that has moved on.
 * Cards from other journeys (booking lists, films, offers) stay active.
 */
export function appendTranscript(items: TranscriptItem[], item: TranscriptItem): TranscriptItem[] {
  const last = items.at(-1);
  if (item.kind === "msg" && last?.kind === "msg" && last.role === item.role && last.text === item.text) return items;
  if (item.kind === "feedback" && items.some((entry) => entry.kind === "feedback")) return items;
  if (item.kind !== "cards") return [...items, item];
  const fingerprint = cardFingerprint(item.ui);
  if (items.some((entry) => entry.kind === "cards" && !entry.archived && cardFingerprint(entry.ui) === fingerprint)) return items;
  const key = journeyKey(item.ui);
  // The same basket step can be rendered twice with different chrome: the widget's own action result
  // (e.g. select_seats: no title, "Add food / Pay") and the agent's follow-up get_order ("Your order",
  // "Pay now / Add food"). Same basket, same card type, same contents → the visible card already says it all.
  if (key) {
    const contents = cardFingerprint({ items: item.ui.items ?? [], meta: item.ui.meta ?? {} });
    if (items.some((entry) => entry.kind === "cards" && !entry.archived && entry.ui.type === item.ui.type && journeyKey(entry.ui) === key && cardFingerprint({ items: entry.ui.items ?? [], meta: entry.ui.meta ?? {} }) === contents)) return items;
  }
  const rank = STEP_RANK[item.ui.type] ?? 0;
  const next = key
    ? items.map((entry) => entry.kind === "cards" && !entry.archived && journeyKey(entry.ui) === key && (STEP_RANK[entry.ui.type] ?? 0) <= rank ? { ...entry, archived: true } : entry)
    : items;
  return [...next, item];
}

/** Everything shown so far becomes read-only (a conversation ended or a basket was cancelled). */
export function freezeCards(items: TranscriptItem[], select: (ui: UiHint) => boolean = () => true): TranscriptItem[] {
  return items.map((entry) => entry.kind === "cards" && !entry.archived && select(entry.ui) ? { ...entry, archived: true } : entry);
}

export function holdSeconds(expiresAt: string | undefined, now = Date.now()): number | null {
  if (!expiresAt || !Number.isFinite(Date.parse(expiresAt))) return null;
  return Math.max(0, Math.ceil((Date.parse(expiresAt) - now) / 1000));
}

export type HoldNoticeSnapshot = { token: string; conversationId: string; epoch: number; userSessionId: string; expiresAtUtc: string };
export type HoldNotice = { kind: "none" | "clear" | "soon" | "expired"; ui?: UiHint };

export function isCurrentHold(expected: HoldNoticeSnapshot, actual: HoldNoticeSnapshot | null): boolean {
  return actual !== null && (Object.keys(expected) as (keyof HoldNoticeSnapshot)[]).every((key) => actual[key] === expected[key]);
}

/** Historical cards can carry old deadlines. Only current server state may produce a hold notice. */
export async function verifyHoldNotice(
  expected: HoldNoticeSnapshot,
  current: () => HoldNoticeSnapshot | null,
  readState: () => Promise<{ conversation: Record<string, any> }>,
  readOrder: () => Promise<CommandResult>,
  now: () => number = Date.now,
): Promise<HoldNotice> {
  const stillCurrent = () => isCurrentHold(expected, current());
  try {
    if (!stillCurrent()) return { kind: "none" };
    const state = await readState();
    if (!stillCurrent() || state.conversation?.id !== expected.conversationId) return { kind: "none" };
    const metadata = state.conversation?.metadata;
    if (!metadata || !Object.hasOwn(metadata, "activeOrder")) return { kind: "none" };
    if (metadata.activeOrder !== expected.userSessionId) return { kind: "clear" };
    const left = holdSeconds(expected.expiresAtUtc, now());
    if (left === null || left > 120) return { kind: "none" };
    if (left > 0) {
      // This silent read must not emit a resume card over an active checkout sheet.
      const booking = metadata.bookingState;
      return { kind: booking?.userSessionId === expected.userSessionId && booking.seatHoldExpiry === expected.expiresAtUtc && ["seats", "payment"].includes(booking.currentJourneyStage) && booking.requiresFreshHold === false ? "soon" : "none" };
    }
    const result = await readOrder();
    if (!stillCurrent() || !result.ok) return { kind: "none" };
    const data = result.data;
    if (data?.paid === true || (data?.active === false && data.expired !== true)) return { kind: "clear" };
    if (data?.expired !== true || data.userSessionId !== expected.userSessionId || data.summary?.expiresAtUtc !== expected.expiresAtUtc) return { kind: "none" };
    return { kind: "expired", ui: result.ui };
  } catch {
    return { kind: "none" };
  }
}

/** Keep acknowledgements factual and exclude credentials and payment tokens. */
export function actionContext(type: string, result: { ok: boolean; speech?: string; data?: Record<string, unknown>; action?: { status: string; type: string; result?: Record<string, unknown> }; error?: string }): string {
  const data = result.data ?? result.action?.result ?? {};
  const safe = Object.fromEntries(Object.entries(data).filter(([key]) => ["bookingState", "summary", "proposal", "proposalRef", "admission", "confirmationId", "seats", "totalCents", "expiresAtUtc", "needs", "combinedCheckout", "bookingReference", "linked", "reason"].includes(key)));
  const scrub = (value: unknown): unknown => Array.isArray(value) ? value.map(scrub) : value && typeof value === "object"
    ? Object.fromEntries(Object.entries(value).filter(([key]) => !/(?:password|token|proof|secret|cvv|authorization)|^(?:pin|otp|email|phone|cardBin|cardNumber|first6)$/i.test(key)).map(([key, nested]) => [key, scrub(nested)]))
    : value;
  const pending = ["queued", "running"].includes(result.action?.status ?? "");
  const succeeded = result.ok && !pending && !["failed", "cancelled"].includes(result.action?.status ?? "");
  return JSON.stringify({ action: type, succeeded, pending, speech: result.speech, state: scrub(safe), error: result.error });
}
